import createDebug from 'debug';
import { Media } from './media';
import { Role, isRole, RoleId } from './role';

import { string, optional, bool, Validator } from './utils';

const debug = createDebug('evp:core:types:user');

export enum UserAccessLevel {
  USER = 'USER',
  MANAGER = 'MANAGER',
  ADMIN = 'ADMINISTRATOR',
}

export function isUserAccessLevel(data?: any): data is UserAccessLevel {
  if (!data || typeof data !== 'string') return false;
  return !!Object.values(UserAccessLevel).find(level => level === data);
}

export enum UserState {
  DELETED = 'DELETED',
  BLOCKED = 'BLOCKED',
  INVITED = 'INVITED',
  ACTIVE = 'ACTIVE',
}

export function isUserState(data?: any): data is UserState {
  if (!data || typeof data !== 'string') return false;
  return !!Object.values(UserState).find(state => state === data);
}

export interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  language: string;

  profilePicture?: Media;

  // organization?: Organization;
  role: Role;
  state: UserState;
  showGetStarted?: boolean;
}

type UserKeys = keyof User;

const userValidator: Record<UserKeys, Validator> = {
  id: string,
  firstName: string,
  lastName: optional<string>(d => typeof d === 'string'),
  email: optional<string>(d => typeof d === 'string'),
  phone: optional<string>(d => typeof d === 'string'),
  language: string,
  profilePicture: optional<Media>((data?: any): data is Media => {
    if (!data || typeof data !== 'object') return false;
    return typeof data.url === 'string';
  }),
  role: isRole,
  state: isUserState,
  showGetStarted: bool,
  // organization: (data?: any): data is Organization => {
  //   if (typeof data !== 'object') return false;
  //   return true;
  // },
};

export function isUser(data?: any): data is User {
  if (!data || typeof data !== 'object') {
    debug('Passed data object is null or not-an-object');
    return false;
  }
  return Object.keys(userValidator).every(key => {
    const valid = userValidator[key as UserKeys](data[key]);
    if (!valid) {
      debug('User key %s is invalid', key);
    }
    return valid;
  });
}

export function isUserSubset(
  data?: any,
  ...props: UserKeys[]
): data is Partial<User> {
  if (typeof data !== 'object') return false;
  return props.every(key => userValidator[key](data[key]));
}

export interface UpdateUser
  extends Pick<
    User,
    'firstName' | 'lastName' | 'email' | 'phone' | 'language'
  > {
  role: RoleId;
  showGetStarted?: boolean;
}
